import React from 'react';

// TSA "Validation approach" on the model scorecard: families 0 and A–F, each showing only
// numbers that were really measured (LIVE / DERIVED), published (PUBLIC BENCHMARK /
// PUBLISHED PRICE) or read from the system (CURRENT STATE). Everything else is shown as
// NOT MEASURED with what produces it. Data: item.validation (backend/validation_view.py).
// Written with React.createElement (no JSX) so it can be render-tested without a build.
const h = React.createElement;

const PROV = {
  LIVE: ['LIVE', '#155724', '#d4edda'],
  DERIVED: ['DERIVED', '#0b5e55', '#e0f5f1'],
  PUBLIC_BENCHMARK: ['PUBLIC BENCHMARK', '#084298', '#e7f0ff'],
  PUBLISHED_PRICE: ['PUBLISHED PRICE', '#0f5c55', '#e6f6f4'],
  CURRENT_STATE: ['CURRENT STATE', '#4a4a8a', '#eeeefa'],
  RECOMMENDATION: ['RECOMMENDATION', '#7a4b00', '#fff0d6'],
  NOT_MEASURED: ['NOT MEASURED', '#777', '#f3f3f3'],
};
const STATUS = {
  MEASURED: ['● Measured', '#155724', '#d4edda'],
  PARTIAL: ['◐ Partly measured', '#856404', '#fff3cd'],
  NOT_MEASURED: ['○ Not measured', '#777', '#f3f3f3'],
};

function Tag({ p }) {
  const [t, fg, bg] = PROV[p] || PROV.NOT_MEASURED;
  return h('span', { style: { fontSize: '9px', fontWeight: 'bold', color: fg, background: bg, border: `1px ${p === 'NOT_MEASURED' ? 'dashed' : 'solid'} ${fg}55`,
    borderRadius: '8px', padding: '0 5px', whiteSpace: 'nowrap', marginLeft: '4px' } }, t);
}

function Metric({ x }) {
  const none = x.provenance === 'NOT_MEASURED';
  const unit = x.unit === '%' ? '%' : x.unit ? ` ${x.unit}` : '';
  return h('div', { style: { fontSize: '11px', padding: '2px 0', color: none ? '#888' : '#222' } },
    h('span', null, `${x.label}: `),
    none ? h('span', { style: { fontStyle: 'italic' } }, 'not measured') : h('strong', null, `${x.value}${unit}`),
    h(Tag, { p: x.provenance }),
    (x.source || x.note || x.how) ? h('div', { style: { fontSize: '10px', color: '#888', marginLeft: '8px' } },
      [x.source, x.note, none && x.how ? `→ ${x.how}` : null].filter(Boolean).join(' · ')) : null);
}

export default function ValidationPanel({ item, getAuthHeaders, canRun, onUpdated }) {
  const v = item && item.validation;
  const [open, setOpen] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  if (!v || v.error) return h('div', { style: { fontSize: '11px', color: '#888' } }, v && v.error ? `Validation view unavailable: ${v.error}` : '');
  const canProbe = (item.bedrock_access && item.bedrock_access.status === 'ALLOWED') ||
    (item.self_host_check && item.self_host_check.deployed && item.self_host_check.deployed.status === 'PASS');
  const probe = async () => {
    if (typeof window !== 'undefined' && !window.confirm(`Run the performance probe on ${item.model_id}? It sends a few dozen short streaming requests at increasing concurrency (a few cents).`)) return;
    setBusy(true); setMsg('Starting performance probe…');
    try {
      const r = await fetch('/api/perf-probe', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ model_id: item.model_id }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      let since = 0;
      for (let i = 0; i < 300; i++) {
        await new Promise(res => setTimeout(res, 3000));
        const jr = await fetch(`/api/evaluate-live/${d.job_id}?since=${since}`, { headers: getAuthHeaders() });
        const job = await jr.json().catch(() => ({}));
        since = job.next || since;
        const last = (job.events || []).slice(-1)[0];
        if (last) setMsg(last.text);
        if (job.status === 'DONE') { setMsg('Performance probe complete.'); if (onUpdated) onUpdated(null); break; }
        if (job.status === 'FAILED') throw new Error(job.error || 'probe failed');
      }
    } catch (e) { setMsg(`❌ ${e.message}`); }
    setBusy(false);
  };
  const c = v.counts || {};
  return h('div', { style: { border: '1px solid #cbd5e0', borderRadius: '6px', padding: '8px', marginBottom: '10px', background: '#fbfdfb' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }, onClick: () => setOpen(!open) },
      h('strong', { style: { fontSize: '12px', color: '#0f2027' } }, `🧪 Validation approach — ${c.MEASURED || 0} measured · ${c.PARTIAL || 0} partly · ${c.NOT_MEASURED || 0} not measured`),
      h('span', { style: { fontSize: '10px', color: '#2b6cb0' } }, open ? '▲ hide' : '▼ show')),
    open ? h('div', { style: { marginTop: '6px' } },
      h('div', { style: { fontSize: '10px', color: '#777', marginBottom: '4px' } }, 'Only real numbers are shown. Anything not yet measured says so, with what measures it.'),
      (v.families || []).map(f => {
        const [st, fg, bg] = STATUS[f.status] || STATUS.NOT_MEASURED;
        return h('div', { key: f.key, style: { borderTop: '1px solid #e2e8f0', padding: '5px 0' } },
          h('div', { style: { display: 'flex', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap' } },
            h('strong', { style: { fontSize: '12px' } }, `${f.key} — ${f.name}`),
            h('span', { style: { fontSize: '10px', fontWeight: 'bold', color: fg, background: bg, borderRadius: '8px', padding: '0 6px' } }, st)),
          h('div', { style: { fontSize: '10px', color: '#666', fontStyle: 'italic', marginBottom: '2px' } }, f.question),
          (f.metrics || []).map((x, i) => h(Metric, { key: i, x })));
      }),
      h('div', { style: { display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' } },
        canRun && canProbe ? h('button', { onClick: probe, disabled: busy, style: { fontSize: '11px', padding: '4px 10px', border: '1px solid #0073bb', color: '#0073bb', background: busy ? '#eee' : '#fff', borderRadius: '4px', cursor: busy ? 'wait' : 'pointer' } },
          busy ? '⏳ Probing…' : (v.perf_measured_at ? '↻ Re-run performance probe' : '⚡ Run performance probe')) : null,
        !canProbe ? h('span', { style: { fontSize: '10px', color: '#888' } }, 'Performance probe needs Bedrock access for this account or a deployed endpoint; open-weight models get it from Evaluate on a temporary GPU.') : null,
        msg ? h('span', { style: { fontSize: '11px', color: msg.startsWith('❌') ? '#721c24' : '#2b6cb0' } }, msg) : null)) : null);
}
